<template>
  <div v-if="holes.length" class="tut-spot">
    <!-- ЭТАЛОННЫЕ ЗОНЫ (zones): затемнение с ОТДЕЛЬНЫМИ дырками через SVG-маску (role hole/both) +
         обводки (role outline/both). Так честно рисуются несколько раздельных окон с разной ролью. -->
    <template v-if="zoneMode">
      <svg class="tut-spot__mask" :width="localW" :height="localH" :viewBox="`0 0 ${localW} ${localH}`" preserveAspectRatio="none">
        <defs>
          <mask :id="maskId">
            <rect x="0" y="0" :width="localW" :height="localH" fill="#fff" />
            <rect
              v-for="(h, i) in holeZones" :key="i"
              :x="h.left" :y="h.top" :width="h.width" :height="h.height" :rx="h.r" :ry="h.r" fill="#000"
            />
          </mask>
        </defs>
        <rect x="0" y="0" :width="localW" :height="localH" fill="rgba(6,3,14,0.72)" :mask="`url(#${maskId})`" />
      </svg>
      <div
        v-for="(h, i) in outlineZones" :key="'o' + i" class="tut-spot__mark"
        :style="{ left: h.left + 'px', top: h.top + 'px', width: h.width + 'px', height: h.height + 'px', borderRadius: h.r + 'px' }"
      ></div>
    </template>
    <!-- цель по селектору/rects: как было — одна общая «дырка» тенью + рамки на каждую цель -->
    <template v-else>
      <div class="tut-spot__hole" :style="holeStyle"></div>
      <div
        v-for="(h, i) in marks" :key="i" class="tut-spot__mark"
        :style="{ left: h.left + 'px', top: h.top + 'px', width: h.width + 'px', height: h.height + 'px' }"
      ></div>
    </template>
    <div class="tut-spot__card" :style="cardStyle">
      <div class="tut-spot__ava">
        <img v-if="portrait" class="tut-spot__face" :src="portrait" :style="faceStyle" alt="">
        <span v-else>{{ mentorIcon }}</span>
      </div>
      <div class="tut-spot__body">
        <div class="tut-spot__who">{{ title }}</div>
        <div class="tut-spot__say">{{ text }}</div>
        <div v-if="counter" class="tut-spot__n">{{ counter }}</div>
      </div>
      <div class="tut-spot__acts">
        <button class="tut-spot__btn" @click="$emit('skip')">{{ $tut('tut.skip') }}</button>
        <button class="tut-spot__btn tut-spot__btn--gold" @click="$emit('next')">{{ nextLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Подсветка элемента интерфейса («дырка» в затемнении + карточка наставника).
 *
 * Цель задаётся CSS-селектором: компонент сам находит элемент, меряет его и переизмеряет на
 * ресайз/скролл. Элемент не найден — оверлей не рисуем совсем (лучше без подсказки, чем подсветка
 * пустого места, если вёрстка изменилась).
 */
import { mentorFace } from './mentor';

let _uid = 0; // уникальный id для SVG-маски (несколько оверлеев на странице не конфликтуют)

export default {
  name: 'TutorialSpotlight',
  props: {
    // CSS-селектор подсвечиваемого элемента; либо `rects` — готовые прямоугольники (canvas-цели)
    target: { type: String, default: '' },
    // [{left, top, width, height}] в координатах вьюпорта: доска рисуется в canvas, DOM-цели у клеток нет
    rects: { type: Array, default: null },
    // ЭТАЛОННЫЕ ЗОНЫ: [{left, top, width, height, role: 'hole'|'outline'|'both', r}] в пикселях вьюпорта.
    // Приоритетнее target/rects: рисуются раздельными дырками (маска) + обводками по роли.
    zones: { type: Array, default: null },
    // положение карточки наставника: 'top' | 'bottom' | '' (авто — под целью/над ней)
    cardPos: { type: String, default: '' },
    title: { type: String, default: '' },
    text: { type: String, default: '' },
    counter: { type: String, default: '' },
    nextLabel: { type: String, default: '' },
    mentorIcon: { type: String, default: '🧙' },
    portrait: { type: String, default: '' },   // спрайт наставника (королева своей фракции)
    pad: { type: Number, default: 10 }, // отступ «дырки» от границ элемента
    // 🔴 «ОДНО ОКНО НАСТАВНИКА»: каждый оверлей шага регистрируется в сторе, и подсказка-помощь по
    // пункту «Пути новичка» молчит, пока открыт хоть один. Оунер ловил дубли диалогов (колесо).
    register: { type: Boolean, default: true },
    // 🔴 СЦЕНА ПАРТИИ ПОВЁРНУТА НА 90° (фейк-ландшафт на телефоне в портрете: `fakeRotated`).
    // Оверлей живёт ВНУТРИ повёрнутого контейнера, поэтому его `position: fixed` отсчитывается уже
    // в ПОВЁРНУТОЙ системе координат, а цели меряются `getBoundingClientRect` — в ЭКРАННОЙ. Без
    // пересчёта карточка наставника и подсветка уезжали за край экрана (баг в полноэкранном режиме
    // Telegram, 2026-08-15: «сообщений наставника почти не видно»).
    rotated: { type: Boolean, default: false },
    // `zones` уже посчитаны В СИСТЕМЕ ОВЕРЛЕЯ (повёрнутая сцена считает их от фрейма через offset*),
    // переводить их из экранных координат не надо — иначе поворот применится дважды.
    zonesLocal: { type: Boolean, default: false },
  },
  emits: ['next', 'skip'],
  data() {
    _uid += 1;
    return {
      box: null, scrolledTo: null, registered: false,
      vw: window.innerWidth, vh: window.innerHeight, maskId: `tut-mask-${_uid}`,
      hostRect: null, // экранный прямоугольник самого оверлея (нужен для повёрнутой сцены)
    };
  },
  computed: {
    faceStyle() { return mentorFace(); },
    // Размеры СИСТЕМЫ КООРДИНАТ оверлея: обычно вьюпорт, в повёрнутой сцене оси меняются местами
    // (контейнер шириной с высоту экрана и наоборот).
    // 🔴 Меряем по САМОМУ оверлею, а не по `innerWidth/innerHeight`: повёрнутый стейдж задан в
    // `dvh/vw`, и в мини-аппе Telegram (шапка, безопасные зоны) эти величины расходятся с окном —
    // затемнение и карточка считались по чужому размеру и уезжали.
    localW() {
      const h = this.hostRect;
      if (h && h.width && h.height) return this.rotated ? h.height : h.width;
      return this.rotated ? this.vh : this.vw;
    },
    localH() {
      const h = this.hostRect;
      if (h && h.width && h.height) return this.rotated ? h.width : h.height;
      return this.rotated ? this.vw : this.vh;
    },
    // режим эталонных зон: рисуем раздельные дырки маской + обводки по роли
    zoneMode() { return !!(this.zones && this.zones.length); },
    holeZones() { return (this.zones || []).filter((z) => z.role === 'hole' || z.role === 'both'); },
    outlineZones() { return (this.zones || []).filter((z) => z.role === 'outline' || z.role === 'both'); },
    // все «окна» (для позиционирования карточки): эталонные зоны / rects / измеренная цель.
    // Все они приходят в ЭКРАННЫХ координатах — `toLocal` переводит их в систему оверлея
    // (в неповёрнутой сцене это то же самое, просто копия).
    holes() {
      if (this.zones && this.zones.length) {
        return this.zonesLocal ? this.zones.map((r) => ({ ...r })) : this.zones.map((r) => this.toLocal(r));
      }
      if (this.rects && this.rects.length) return this.rects.map((r) => this.toLocal(r));
      return this.box ? [this.toLocal(this.box)] : [];
    },
    // общий прямоугольник всех целей — по нему прорезаем затемнение
    holeStyle() {
      const list = this.holes;
      const left = Math.min(...list.map((r) => r.left));
      const top = Math.min(...list.map((r) => r.top));
      const right = Math.max(...list.map((r) => r.left + r.width));
      const bottom = Math.max(...list.map((r) => r.top + r.height));
      return {
        left: `${left - this.pad}px`,
        top: `${top - this.pad}px`,
        width: `${right - left + this.pad * 2}px`,
        height: `${bottom - top + this.pad * 2}px`,
      };
    },
    // отдельные обводки: нужны, когда целей несколько (например, две фигуры с даром)
    marks() { return this.holes.length > 1 ? this.holes : []; },
    // карточку ставим под целью, а если места нет — над ней (мобильный экран короткий).
    // Оба варианта КЛАМПИМ во вьюпорт: у высокой цели на 390px карточка иначе уезжала за край экрана.
    cardStyle() {
      // 🔴 ШИРИНА — ПО СИСТЕМЕ ОВЕРЛЕЯ, А НЕ ПО `vw` (баг оунера 2026-08-17: в мини-аппе длинные
      // реплики наставника читались не полностью). В повёрнутой сцене `92vw` — это 92% УЗКОЙ стороны
      // телефона, хотя карточка лежит вдоль длинной: текст сжимался в колонку и не влезал по высоте.
      const w = { width: `${Math.min(560, Math.round(this.localW * 0.92))}px` };
      // явное положение карточки (эталонный шаг): сверху/снизу экрана, не перекрывая доску/кнопки
      if (this.cardPos === 'top') return { ...w, top: '10px' };
      if (this.cardPos === 'bottom') return { ...w, bottom: '10px' };
      const list = this.holes;
      const top = Math.min(...list.map((r) => r.top));
      const bottom = Math.max(...list.map((r) => r.top + r.height));
      const b = { top, height: bottom - top };
      const h = this.localH;  // высота СИСТЕМЫ оверлея (в повёрнутой сцене это ширина экрана)
      const below = h - (b.top + b.height);
      if (below > 210) return { ...w, top: `${Math.min(b.top + b.height + 18, Math.max(8, h - 190))}px` };
      return { ...w, bottom: `${Math.min(Math.max(h - b.top + 18, 8), Math.max(8, h - 190))}px` };
    },
  },
  watch: {
    // новая остановка экскурсии — заново подвести цель к центру экрана
    target() { this.scrolledTo = null; this.measure(); },
    // 🔴 Регистрируем ФАКТ ОТРИСОВКИ, а не монтирование: корневой `v-if="holes.length"` живёт внутри
    // компонента, и ненайденная цель означает «на экране пусто» — иначе помощь по «Пути новичка»
    // молчала бы на экране, где подсветка шага ничего не нашла.
    holes: {
      handler(list) { this.setRegistered(!!(list && list.length)); },
      immediate: true,
    },
  },
  mounted() {
    this.measure();
    this.timer = setInterval(this.measure, 400); // элемент может появиться/сдвинуться (загрузка данных)
    window.addEventListener('resize', this.measure);
    window.addEventListener('scroll', this.measure, true);
  },
  beforeUnmount() {
    this.setRegistered(false);
    clearInterval(this.timer);
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('scroll', this.measure, true);
  },
  methods: {
    // отметиться в сторе как «открытое окно наставника» (счётчик читает TutorialHelp)
    setRegistered(on) {
      if (!this.register || this.registered === !!on) return;
      this.registered = !!on;
      this.$store.commit(on ? 'SPOT_OPEN' : 'SPOT_CLOSE');
    },
    /**
     * Экранный прямоугольник → координаты СИСТЕМЫ ОВЕРЛЕЯ.
     *
     * Сцена партии на телефоне в портрете повёрнута на 90° по часовой (`.play-web__stage--rotated`),
     * а `transform` делает контейнер точкой отсчёта для `position: fixed` внутри. Считаем перевод по
     * собственному прямоугольнику оверлея — знать угол и размеры окна не нужно, достаточно того, что
     * поворот ровно на 90°: локальная ось X идёт вниз по экрану, ось Y — справа налево.
     */
    toLocal(r) {
      const out = { left: r.left, top: r.top, width: r.width, height: r.height, role: r.role, r: r.r };
      if (!this.rotated) return out;
      const host = this.hostRect;
      if (!host) return out;
      return {
        left: r.top - host.top,
        top: host.width - (r.left - host.left) - r.width,
        width: r.height,
        height: r.width,
        role: r.role,
        r: r.r,
      };
    },
    measure() {
      this.vw = window.innerWidth; this.vh = window.innerHeight; // размер маски затемнения
      // собственный прямоугольник: перевод координат целей в повёрнутой сцене И размер системы
      // координат оверлея (в фуллскрине мини-аппа он не равен окну браузера)
      const host = this.$el && this.$el.getBoundingClientRect ? this.$el.getBoundingClientRect() : null;
      const sameHost = host && this.hostRect
        && ['left', 'top', 'width', 'height'].every((k) => Math.abs(this.hostRect[k] - host[k]) < 1);
      if (!sameHost) {
        this.hostRect = host ? { left: host.left, top: host.top, width: host.width, height: host.height } : null;
      }
      if (this.zones && this.zones.length) { this.box = null; return; } // эталонные зоны заданы напрямую
      if (this.rects && this.rects.length) { this.box = null; return; } // цели заданы прямоугольниками
      if (!this.target) { this.box = null; return; }
      const el = document.querySelector(this.target);
      if (!el) { this.box = null; return; }
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) { this.box = null; return; }
      // цель за пределами экрана (длинная страница, телефон) — подводим её к центру:
      // иначе игрок видит затемнение без подсвеченного места и не понимает, чего от него хотят.
      // 🔴 ПРОКРУТКА НЕ РАЗОВАЯ (фидбек оунера 2026-08-04): в профиле ряд кнопок «уезжает» вниз ПОСЛЕ
      // первой подводки — фото/ресурсы/рефералка дорисовываются позже и сдвигают вёрстку, а прежний
      // разовый замок `scrolledTo` больше не подводил, и на мобилке была видна только реплика наставника.
      // Первый раз подводим на ЧАСТИЧНЫЙ выход; повторно — только если цель уехала ПОЛНОСТЬЮ за экран
      // (частично видимую высокую цель НЕ дёргаем — не воюем со smooth-скроллом). Троттл 600 мс.
      const partlyOut = r.top < 0 || r.bottom > window.innerHeight;
      const fullyOut = r.bottom <= 0 || r.top >= window.innerHeight;
      const firstBring = this.scrolledTo !== this.target && partlyOut;
      if (firstBring || fullyOut) {
        const now = performance.now();
        if (firstBring || now - (this.lastScrollAt || 0) > 600) {
          this.scrolledTo = this.target;
          this.lastScrollAt = now;
          try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { el.scrollIntoView(); }
        }
        return; // размеры доснимет следующий тик таймера, когда прокрутка закончится
      }
      const box = { left: r.left, top: r.top, width: r.width, height: r.height };
      const same = this.box && ['left', 'top', 'width', 'height'].every((k) => Math.abs(this.box[k] - box[k]) < 1);
      if (!same) this.box = box;
    },
  },
};
</script>

<style scoped lang="scss">
.tut-spot {
  position: fixed;
  inset: 0;
  z-index: 900;
  pointer-events: none; // сам оверлей кликов не перехватывает: цель остаётся нажимаемой

  // затемнение с раздельными дырками (эталонные зоны): SVG-маска на весь вьюпорт
  &__mask {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &__hole {
    position: absolute;
    border-radius: 14px;
    // «дырка»: затемняем ВСЁ вокруг рамки одной огромной тенью
    box-shadow: 0 0 0 9999px rgba(6, 3, 14, 0.72), 0 0 34px rgba(255, 190, 70, 0.35) inset;
    border: 2px solid rgba(255, 210, 120, 0.85);
    animation: tut-pulse 1.6s ease-in-out infinite;
  }

  &__mark {
    position: absolute;
    border-radius: 12px;
    border: 2px solid rgba(255, 210, 120, 0.9);
    box-shadow: 0 0 22px rgba(255, 190, 70, 0.35);
  }

  &__card {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: min(560px, 92vw);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 15px;
    border-radius: 14px;
    border: 1px solid rgba(255, 200, 90, 0.5);
    background: linear-gradient(140deg, rgba(60, 42, 30, 0.96), rgba(30, 20, 44, 0.97));
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
    pointer-events: auto;
  }
  &__ava {
    width: 46px; height: 46px; flex: 0 0 auto; border-radius: 50%; overflow: hidden;
    display: flex; align-items: center; justify-content: center; font-size: 22px;
    border: 2px solid rgba(255, 210, 120, 0.6);
    background: radial-gradient(circle at 40% 30%, #6b4b8f, #2a1c3f);
  }
  &__face { width: 100%; height: 100%; display: block; }
  &__body { flex: 1; min-width: 0; }
  &__who { font-size: 0.78em; font-weight: 700; color: #ffcf94; }
  &__say { font-size: 0.92em; color: #f6ecd2; line-height: 1.35; margin-top: 3px; }
  &__n { font-size: 0.72em; color: #b3a5d8; margin-top: 5px; }
  &__acts { display: flex; flex-direction: column; gap: 7px; }
  &__btn {
    font-family: inherit; font-size: 0.8em; white-space: nowrap;
    border-radius: 10px; padding: 7px 14px; cursor: pointer;
    border: 1px solid rgba(150, 130, 220, 0.45); background: rgba(40, 27, 66, 0.75); color: #ded3f5;
    &--gold {
      border-color: rgba(255, 200, 90, 0.6); color: #3a2500; font-weight: 700;
      background: linear-gradient(135deg, #ffd257, #f0a13a);
    }
  }
}

@keyframes tut-pulse {
  0%, 100% { box-shadow: 0 0 0 9999px rgba(6, 3, 14, 0.72), 0 0 24px rgba(255, 190, 70, 0.3) inset; }
  50% { box-shadow: 0 0 0 9999px rgba(6, 3, 14, 0.72), 0 0 40px rgba(255, 190, 70, 0.5) inset; }
}

@media (max-width: 720px) {
  .tut-spot__card { flex-wrap: wrap; padding: 10px 12px; gap: 9px; }
  .tut-spot__acts { flex-direction: row; width: 100%; justify-content: flex-end; }
}
</style>
